// JWT payload を verify せずに参照するためのユーティリティ。
// 主に access token の `exp` claim を取って proactive refresh のタイマーを
// 仕込む用途で使う。署名検証は API 側で行うので、クライアントはペイロード
// を信用せず「期限切れの可能性が高いタイミング」を推定するだけに留める。
//
// base64url を `atob` で復号するため、追加パッケージは不要。

export interface JwtPayload {
  exp?: number;
  iat?: number;
  sub?: string;
  [key: string]: unknown;
}

function base64UrlDecode(input: string): string {
  // base64url → base64 への変換
  let s = input.replace(/-/g, '+').replace(/_/g, '/');
  // パディング補完
  const pad = s.length % 4;
  if (pad === 2) s += '==';
  else if (pad === 3) s += '=';
  else if (pad !== 0) {
    // pad === 1 は不正
    throw new Error('invalid base64url length');
  }
  // atob は Latin-1 文字列を返すので、UTF-8 の payload に対応するため
  // %エスケープ経由で decodeURIComponent する。
  const binary = atob(s);
  try {
    return decodeURIComponent(
      binary
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join(''),
    );
  } catch {
    return binary;
  }
}

export function decodeJwtPayload(token: string): JwtPayload | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  const payloadPart = parts[1];
  if (!payloadPart) return null;
  try {
    const json = base64UrlDecode(payloadPart);
    const obj = JSON.parse(json);
    if (typeof obj !== 'object' || obj === null) return null;
    return obj as JwtPayload;
  } catch {
    return null;
  }
}

// 期限切れまでの残り秒数を返す。トークンが無効/exp 無しなら null。
export function getTokenSecondsRemaining(token: string | null | undefined): number | null {
  if (!token) return null;
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') return null;
  const nowSec = Math.floor(Date.now() / 1000);
  return payload.exp - nowSec;
}

// 既に期限切れ(または exp が読み取れない)なら true。
export function isTokenExpired(token: string | null | undefined): boolean {
  const remaining = getTokenSecondsRemaining(token);
  if (remaining === null) return true;
  return remaining <= 0;
}
